import weekSelectPlugin from "flatpickr/dist/plugins/weekSelect/weekSelect";
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import type { Plugin } from "flatpickr/dist/types/options";

export const formatISODate = (date: Date) => date.toISOString().slice(0, 10);

export const ensureDateValue = (value: string | null | undefined, fallback: string) => {
  if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  return fallback;
};

export const getISOWeekString = (date: Date) => {
  const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNumber = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNumber);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((target.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${target.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
};

export const getDateOfISOWeek = (week: number, year: number) => {
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dayOfWeek = simple.getUTCDay();
  const isoWeekStart = new Date(simple);
  const diff = (dayOfWeek + 6) % 7;
  isoWeekStart.setUTCDate(simple.getUTCDate() - diff);
  return isoWeekStart;
};

export const getWeekRangeFromValue = (
  value: string,
  fallback: string,
): { start: string; end: string } => {
  const fallbackDate = new Date(`${fallback}T00:00:00`);
  const match = /^(\d{4})-W(\d{2})$/.exec(value ?? "");
  if (!match) {
    const fallbackValue = getISOWeekString(fallbackDate);
    return getWeekRangeFromValue(fallbackValue, fallback);
  }
  const year = Number(match[1]);
  const week = Number(match[2]);
  const isoStart = getDateOfISOWeek(week, year);
  const start = new Date(isoStart);
  start.setUTCDate(start.getUTCDate() - 1);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  return { start: formatISODate(start), end: formatISODate(end) };
};

export const getMonthRangeFromValue = (value: string, fallback: string) => {
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  const baseDate = match
    ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1))
    : new Date(`${fallback}T00:00:00`);
  const start = formatISODate(baseDate);
  const endDate = new Date(Date.UTC(baseDate.getUTCFullYear(), baseDate.getUTCMonth() + 1, 0));
  const end = formatISODate(endDate);
  const previousDate = formatISODate(
    new Date(Date.UTC(baseDate.getUTCFullYear(), baseDate.getUTCMonth(), 0)),
  );
  return { start, end, previousDate };
};

export const normalizeCustomRange = (from: string, to: string, fallback: string) => {
  const startCandidate = ensureDateValue(from, fallback);
  const endCandidate = ensureDateValue(to, startCandidate);
  if (endCandidate < startCandidate) {
    return { start: endCandidate, end: startCandidate };
  }
  return { start: startCandidate, end: endCandidate };
};

export const parseIsoDate = (value?: string | null) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getWeekAnchorDate = (value: string, fallback: string) => {
  const { start } = getWeekRangeFromValue(value, fallback);
  return parseIsoDate(start);
};

export const getMonthAnchorDate = (value: string, fallback: string) => {
  const { start } = getMonthRangeFromValue(value, fallback);
  return parseIsoDate(start);
};

export const createWeekSelectPlugin = () => weekSelectPlugin() as Plugin<object>;

export const createMonthSelectPlugin = (
  config: Parameters<typeof monthSelectPlugin>[0],
) => monthSelectPlugin(config) as Plugin<object>;

export const formatMonthYear = (value: string) => {
  if (!/^\d{4}-\d{2}$/.test(value)) {
    return "";
  }
  const date = new Date(`${value}-01T00:00:00`);
  return date.toLocaleDateString("es-PY", {
    year: "numeric",
    month: "long",
  });
};
